import type { AppRouter } from "@brewbase/api";
import {
  createTRPCQueryUtils,
  createTRPCReact,
  type inferReactQueryProcedureOptions,
} from "@trpc/react-query";
import type { inferRouterOutputs, inferRouterInputs } from "@trpc/server";
import {
  httpBatchLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { queryClient } from "./query";

export const trpc = createTRPCReact<AppRouter>();

export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export const trpcClient = (() => {
  const url = new URL(window.location.origin);
  url.pathname = "/trpc";

  const client = trpc.createClient({
    links: [
      loggerLink(),
      // Subscriptions ride SSE (no separate WS server); everything else batches.
      splitLink({
        condition: (op) => op.type === "subscription",
        true: httpSubscriptionLink({ url: url.toString(), transformer: superjson }),
        // Batched GETs carry every input in the URL; past Node's 16 KB header
        // limit the server answers 431 and the whole batch fails. Split first.
        false: httpBatchLink({ url, transformer: superjson, maxURLLength: 8000 }),
      }),
    ],
  });

  return client;
})();

export const trpcQuery = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

export const trpcUtils = createTRPCQueryUtils({
  queryClient,
  client: trpcClient,
});
